import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Map, Mail, Loader2 } from 'lucide-react';

import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Privacy Settings page — /settings/privacy.
 *
 * Three independent toggles, all persisted to the current user via
 * PUT /api/user/privacy. Values default to the "most discoverable" state
 * for legacy accounts (matches the User model defaults).
 */
const PrivacySettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    profile_visibility: 'public',
    show_on_map: true,
    discoverable_by_email: true,
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/privacy`, { withCredentials: true });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to load privacy settings:', err);
      toast.error('Could not load privacy settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const patch = async (updates) => {
    // Optimistic update — snap the UI immediately so toggles feel instant.
    const previous = settings;
    const next = { ...settings, ...updates };
    setSettings(next);
    setSaving(true);
    try {
      await axios.put(`${API}/user/privacy`, updates, { withCredentials: true });
      toast.success('Privacy updated');
    } catch (err) {
      console.error('Failed to update privacy:', err);
      toast.error(err.response?.data?.detail || 'Could not update privacy');
      setSettings(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="privacy-page-loading">
        <Navigation />
        <div className="flex items-center justify-center pt-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <Navigation />
      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-2xl mx-auto space-y-8">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-4"
              onClick={() => navigate('/settings')}
              data-testid="privacy-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Settings
            </Button>
            <h1 className="text-3xl font-bold mb-2">Privacy</h1>
            <p className="text-muted-foreground">
              Control how visible your profile is to other members.
            </p>
          </div>

          <div className="rounded-2xl bg-card border border-border/50 overflow-hidden divide-y divide-border/50">
            {/* Profile visibility */}
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <Label className="text-base font-medium">Profile visibility</Label>
                  <p className="text-sm text-muted-foreground">
                    Who can see your full profile page.
                  </p>
                </div>
              </div>
              <Select
                value={settings.profile_visibility}
                onValueChange={(v) => patch({ profile_visibility: v })}
                disabled={saving}
              >
                <SelectTrigger className="w-full" data-testid="visibility-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public" data-testid="visibility-public">
                    Public — anyone signed in can view
                  </SelectItem>
                  <SelectItem value="members" data-testid="visibility-members">
                    Members only — hidden from guests (future)
                  </SelectItem>
                  <SelectItem value="private" data-testid="visibility-private">
                    Private — only you can view your profile
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Show on map */}
            <div className="flex items-center justify-between gap-4 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Map className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <Label className="text-base font-medium">Show me on the map</Label>
                  <p className="text-sm text-muted-foreground">
                    Let other members see your general location on the discovery map.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.show_on_map}
                onCheckedChange={(v) => patch({ show_on_map: v })}
                disabled={saving}
                data-testid="show-on-map-switch"
              />
            </div>

            {/* Discoverable by email */}
            <div className="flex items-center justify-between gap-4 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <Label className="text-base font-medium">Findable by email</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow other members to find your profile by searching your email.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.discoverable_by_email}
                onCheckedChange={(v) => patch({ discoverable_by_email: v })}
                disabled={saving}
                data-testid="discoverable-switch"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacySettings;
